import { Component, OnInit, OnDestroy, TemplateRef, ViewChild, ViewEncapsulation } from '@angular/core';
import { BsModalService, BsModalRef } from 'ngx-bootstrap/modal';
import { NotificationService } from '@service/notification.service';
import { TranslateService } from '@ngx-translate/core';
import { CalendarService } from '@service/rest/calendar/calendar.service';
import { forkJoin, interval, Observable, of, Subscription } from 'rxjs';
import { SocketEventStorage } from '@storage/socket-event.storage';
import { SNOOZE_OPTIONS } from './snooze-options';
import { UserSettingsStorage } from '@storage/settings/user-settings.storage';
import * as moment from 'moment';
import { Router } from '@angular/router';
import { catchError, first, map } from 'rxjs/operators';
import { BannerService } from '@service/banner.service';
import { LocalSettingsKey } from '@shared/types/local-settings';
import { SanitizeAndEncodeHtmlPipe } from '@shared/pipes/sanitizeAndEncodeHtml.pipe';
import { RemindersStorage } from '@storage/calendar/reminders.storage';
import { MailboxInvitationRefreshersStorage } from '@storage/mailbox/mailbox-invitation-refreshers.storage';
import { CalendarEventsRefreshersStorage } from '@storage/calendar/calendar-events-refreshers.storage';
declare var $: any;

@Component({
  selector: 'app-reminder',
  templateUrl: './reminder.component.html',
  styleUrls: ['./reminder.component.scss'],
  encapsulation: ViewEncapsulation.None,
  providers: [SanitizeAndEncodeHtmlPipe],
})
export class ReminderComponent implements OnInit, OnDestroy {
  currentTime: any;
  remindersList: any[] = [];
  modalRef: BsModalRef;
  isAlarmsLoading: boolean = false;
  modalOpened: boolean = false;
  alarmsList: any[] = [];
  locale: string = localStorage.getItem(LocalSettingsKey.TRANSLATE) || 'tr';
  refreshSubscription: Subscription;
  socketSubscription: Subscription;
  modalOpenedSubscription: Subscription;
  intervalSource = interval(1000);
  intervalSubscription: Subscription;
  eventsSubscription: Subscription;
  remindersSwitchSubscription: Subscription;
  calendarListSubscription: Subscription;
  alarmsSubscription: Subscription;
  snoozeOptions = SNOOZE_OPTIONS;
  hover: boolean = false;
  selectedReminders: any[] = [];
  @ViewChild('template') template: TemplateRef<any>;

  constructor(
    private modalService: BsModalService,
    private calendarService: CalendarService,
    private notificationService: NotificationService,
    private translateService: TranslateService,
    private userSettingsStorage: UserSettingsStorage,
    private socketEventStorage: SocketEventStorage,
    private remindersStorage: RemindersStorage,
    private calendarEventsRefreshersStorage: CalendarEventsRefreshersStorage,
    private mailboxInvitationRefreshersStorage: MailboxInvitationRefreshersStorage,
    private router: Router,
    private bannerService: BannerService
  ) {
    $(window).on('storage', this.messageReceive);
    this.userSettingsStorage.listenLanguageChange().subscribe(data => {
      if (data) {
        this.changeMomentTranslate(data);
      }
    });
  }

  ngOnInit(): void {
    this.getUserSettings();
    this.listenRemindersSwitchRefresher();
    moment.locale(this.locale);
  }

  changeMomentTranslate(language: string): void {
    this.locale = language;
    moment.locale(language);
    this.updateReminders();
  }

  ngOnDestroy() {
    this.unsubscribeAll();
  }

  messageReceive(ev): void {
    if (ev.originalEvent.key === LocalSettingsKey.LOGGED_OUT) {
      this.unsubscribeAll();
    }
  }

  unsubscribeAll(): void {
    if (this.modalRef) {
      this.modalRef.hide();
    }
    this.remindersSwitchSubscription?.unsubscribe();
    this.removeRemindersListeners();
  }

  getUserSettings(): void {
    if (!this.bannerService.isBannerSeen()) {
      return;
    }
    this.getStorageUserSettings();
  }

  getStorageUserSettings(): void {
    this.userSettingsStorage
      .getUserSettings()
      .pipe(first(data => (data.meeting_reminder || 0) === 1))
      .subscribe(data => {
        this.addRemindersListeners();
        if (data.language !== this.locale) {
          this.changeMomentTranslate(data.language);
        }
      });
  }

  listenRemindersSwitchRefresher(): void {
    this.remindersSwitchSubscription = this.userSettingsStorage.listenRemindersSwitchRefresher().subscribe(data => {
      if (!data) {
        return;
      }
      if (data === 'true') {
        this.addRemindersListeners();
      } else {
        this.close();
        this.removeRemindersListeners();
      }
    });
  }

  addRemindersListeners(): void {
    this.listenRefresher();
    this.listenSocketEvents();
    this.listenReminderModalOpenedRefresher();
    this.listenEventsRefresher();
    this.getCalendars(true);
    this.getNextReminders();
  }

  removeRemindersListeners(): void {
    this.refreshSubscription?.unsubscribe();
    this.socketSubscription?.unsubscribe();
    this.modalOpenedSubscription?.unsubscribe();
    this.intervalSubscription?.unsubscribe();
    this.eventsSubscription?.unsubscribe();
    this.calendarListSubscription?.unsubscribe();
    this.alarmsSubscription?.unsubscribe();
  }

  getCalendars(openModal): void {
    const _this = this;
    const start = moment().subtract(1, 'weeks').valueOf();
    const end = moment().endOf('day').valueOf();
    this.isAlarmsLoading = true;
    this.calendarListSubscription = this.calendarService.getCalendars().subscribe({
      next: data => {
        const requests: Observable<any>[] = [];
        const alarmsList = [];
        data.map(calendar => {
          requests.push(_this.getAlarms(calendar.id, alarmsList, start, end));
        });

        this.isAlarmsLoading = requests.length !== 0;
        this.alarmsSubscription = forkJoin(requests).subscribe({
          next: responseList => {
            this.isAlarmsLoading = false;
            const errorResponse = responseList.find(response => response && response['error']);
            if (errorResponse) {
              return;
            }
            this.alarmsList = alarmsList;
            _this.getPreviousReminders(openModal);
          },
          error: (err: any) => {
            this.isAlarmsLoading = false;
          },
        });
      },
      error: (err: any) => {
        this.isAlarmsLoading = false;
      },
    });
  }

  getAlarms(id: number, alarmsList, startTime?: number, endTime?: number): Observable<any> {
    return this.calendarService.getAlarms(id, startTime, endTime).pipe(
      map(data => {
        data.map(el => {
          if (el.alarm) {
            el.alarm_info['relative_time'] = moment(el.alarm_info.alarm_instance_start).locale(this.locale).fromNow();
            alarmsList.push(el);
          }
        });
      }),
      catchError(error => of('error', error))
    );
  }

  navigateToEventDetail(reminder): void {
    let mainUrl = '/c/event-detail';
    let queryParams = {};
    queryParams = {
      id: reminder.alarm_info.invite_id,
    };
    if (reminder.is_organizer) {
      mainUrl = 'c/create-event';
    }
    if (reminder.recur) {
      queryParams = {
        id: reminder.alarm_info.invite_id,
        start: reminder.alarm_instance_start,
        end: reminder.alarm_instance_end,
        recur_date: reminder.alarm_recur_date,
        all_instances: false,
      };
    }
    this.router.navigate([mainUrl], { queryParams: queryParams });
    this.close();
  }

  getPreviousReminders(openModal): void {
    const _this = this;
    const remindersList = [];
    const previousAlarms = this.alarmsList.filter(function (alarm) {
      return (
        new Date(moment(alarm.alarm_info.next_alarm).format('YYYY-MM-DDTHH:mm')).getTime() <= new Date(moment().format('YYYY-MM-DDTHH:mm')).getTime()
      );
    });
    previousAlarms.map(previousAlarm => {
      if (
        new Date(moment(previousAlarm.alarm_info.alarm_instance_start).format('YYYY-MM-DDTHH:mm')).getTime() <
        new Date(moment().format('YYYY-MM-DDTHH:mm')).getTime()
      ) {
        previousAlarm.alarm_info['relative_time'] = moment(previousAlarm.alarm_info.alarm_instance_start).locale(this.locale).fromNow(true);
        previousAlarm.alarm_info['relative_key'] = 'OVERDUE';
        previousAlarm['inPast'] = true;
      }
      if (
        new Date(moment(previousAlarm.alarm_info.alarm_instance_start).format('YYYY-MM-DDTHH:mm')).getTime() ===
        new Date(moment().format('YYYY-MM-DDTHH:mm')).getTime()
      ) {
        previousAlarm.alarm_info['relative_time'] = '';
        previousAlarm.alarm_info['relative_key'] = 'NOW';
        previousAlarm['inPast'] = false;
      }
      remindersList.push(previousAlarm);
    });
    this.remindersList = remindersList;
    this.remindersStorage.setReminders(this.remindersList);
    this.remindersStorage.setRemindersRefresher(true);
    if (this.remindersList.length !== 0) {
      if (!_this.modalOpened) {
        _this.resetSelectedReminders();
        _this.select(true, _this.remindersList[0]);
      } else {
        _this.getPreviousCheckedAlarms(_this.remindersList);
      }
      this.controlSnoozeOptions();
      if (this.modalOpened === false && openModal) {
        this.openModal(this.template);
      }
    } else {
      if (this.modalOpened) {
        this.close();
      }
    }
  }

  getPreviousCheckedAlarms(remindersList): void {
    if (remindersList.length === 1) {
      this.select(true, remindersList[0]);
    } else {
      for (let i = 0; i < this.selectedReminders.length; i++) {
        if (remindersList.filter(reminder => reminder.appointment_id === this.selectedReminders[i].appointment_id).length === 0) {
          this.selectedReminders = this.selectedReminders.filter(
            selectedReminder => selectedReminder.appointment_id !== this.selectedReminders[i].appointment_id
          );
        }
      }
    }

    for (let i = 0; i < remindersList.length; i++) {
      for (let k = 0; k < this.selectedReminders.length; k++) {
        if (remindersList[i].appointment_id === this.selectedReminders[k].appointment_id) {
          remindersList[i].checked = this.selectedReminders[k].checked;
          break;
        }
      }
    }
  }

  /**
   * Checks reminders at 00 minute to show if there is any reminder at that time
   */
  getNextReminders(): void {
    const _this = this;
    this.intervalSubscription = this.intervalSource.subscribe(() => {
      if (moment().format('ss') === '00') {
        const nextAlarms = _this.alarmsList.filter(
          alarm =>
            new Date(moment(alarm.alarm_info.next_alarm).format('YYYY-MM-DDTHH:mm')).getTime() ===
            new Date(moment().format('YYYY-MM-DDTHH:mm')).getTime()
        );
        nextAlarms.map(nextAlarm => {
          if (_this.remindersList.filter(reminder => reminder.appointment_id === nextAlarm.appointment_id).length === 0) {
            _this.remindersList.unshift(nextAlarm);
          }
        });
        if (nextAlarms.length !== 0) {
          if (!_this.modalOpened) {
            _this.resetSelectedReminders();
            _this.select(true, _this.remindersList[0]);
          } else {
            _this.getPreviousCheckedAlarms(_this.remindersList);
          }
          if (_this.modalOpened === false) {
            _this.openModal(_this.template);
          }
        }
        _this.updateReminders();
      }
    });
  }

  resetSelectedReminders(): void {
    this.selectedReminders = [];
    this.remindersList.map(reminder => {
      reminder.checked = false;
    });
  }

  /**
   * Updates reminders' relative time
   */
  updateReminders(): void {
    this.remindersList.map(reminder => {
      if (
        new Date(moment(reminder.alarm_info.alarm_instance_start).format('YYYY-MM-DDTHH:mm')).getTime() <
        new Date(moment().format('YYYY-MM-DDTHH:mm')).getTime()
      ) {
        reminder.alarm_info['relative_time'] = moment(reminder.alarm_info.alarm_instance_start).locale(this.locale).fromNow(true);
        reminder.alarm_info['relative_key'] = 'OVERDUE';
        reminder['inPast'] = true;
      }
      if (
        new Date(moment(reminder.alarm_info.alarm_instance_start).format('YYYY-MM-DDTHH:mm')).getTime() ===
        new Date(moment().format('YYYY-MM-DDTHH:mm')).getTime()
      ) {
        reminder.alarm_info['relative_time'] = '';
        reminder.alarm_info['relative_key'] = 'NOW';
        reminder['inPast'] = false;
      }
      if (
        new Date(moment(reminder.alarm_info.alarm_instance_start).format('YYYY-MM-DDTHH:mm')).getTime() >
        new Date(moment().format('YYYY-MM-DDTHH:mm')).getTime()
      ) {
        reminder.alarm_info['relative_time'] = moment(reminder.alarm_info.alarm_instance_start).locale(this.locale).fromNow(true);
        reminder.alarm_info['relative_key'] = '';
        reminder['inPast'] = false;
      }
    });
    this.controlSnoozeOptions();
  }

  openModal(template: TemplateRef<any>): void {
    this.modalRef = this.modalService.show(
      template,
      Object.assign({
        backdrop: 'static',
        keyboard: false,
        class: 'modal-medium reminder-modal',
      })
    );
    this.modalOpened = true;
    moment.locale(this.locale);
    this.updateReminders();
  }

  close(): void {
    this.modalRef?.hide();
    this.modalOpened = false;
  }

  /**
   * Controls if there is any recurrence event.
   * If recurrence event time smaller than now, it dismiss the event because system needs to show recurrence event's next alarm.
   */
  controlRecurrenceEvents(): void {
    const snoozedReminders = [];
    this.remindersList.map(reminder => {
      if (reminder.recur && new Date(reminder.alarm_info.alarm_instance_start).getTime() < new Date().getTime()) {
        snoozedReminders.push(reminder);
      }
    });
    if (snoozedReminders.length !== 0) {
      this.dismiss(snoozedReminders);
      this.close();
    }
  }

  snoozeReminders(duration?, type?, negativeDuration?): void {
    if (this.remindersList.length === 1) {
      this.selectedReminders[0] = this.remindersList[0];
      this.remindersList[0].checked = true;
    }
    const snoozedReminders = [];
    if (duration !== null && type !== null && negativeDuration !== null) {
      switch (negativeDuration) {
        case 1:
          this.selectedReminders.map(selectedReminder => {
            const reminder = {
              invite_id: selectedReminder.alarm_info.invite_id,
              snooze_time: moment(selectedReminder.alarm_info.next_alarm).subtract(duration, type).valueOf(),
            };
            snoozedReminders.push(reminder);
          });
          break;
        default: // 0
          this.selectedReminders.map(selectedReminder => {
            const reminder = {
              invite_id: selectedReminder.alarm_info.invite_id,
              snooze_time: moment(new Date()).add(duration, type).valueOf(),
            };
            snoozedReminders.push(reminder);
          });
      }
    } else {
      this.selectedReminders.map(selectedReminder => {
        const reminder = {
          invite_id: selectedReminder.alarm_info.invite_id,
          snooze_time: moment(selectedReminder.alarm_info.alarm_instance_start).valueOf(),
        };
        snoozedReminders.push(reminder);
      });
    }
    const formData = {
      appt: snoozedReminders,
    };
    this.snooze(formData);
  }

  snooze(formData): void {
    this.calendarService.snoozeAlarm(formData).subscribe(
      data => {
        this.getCalendars(false);
        let successMessage = 'SUCCESS_ALARM_SNOOZED';
        if (formData.appt.length > 1) {
          successMessage = 'SUCCESS_ALARMS_SNOOZED';
        }
        this.notificationService.showSuccess(this.translateService.instant(successMessage));
        this.remindersList = this.remindersList.filter(x => !x.checked);
        this.selectedReminders = [];
        if (this.remindersList.length === 0) {
          this.close();
        } else {
          this.selectedReminders[0] = this.remindersList[0];
          this.remindersList[0].checked = true;
        }
      },
      error => this.notificationService.showError(this.translateService.instant(error.error.errors.message))
    );
  }

  dismiss(selectedRemindersList): void {
    const selectedReminders = selectedRemindersList;
    if (this.remindersList.length === 1) {
      selectedReminders[0] = this.remindersList[0];
    }
    const dismissedReminders = [];
    let reminder;
    selectedReminders.map(selectedReminder => {
      reminder = {
        invite_id: selectedReminder.alarm_info.invite_id,
        dismiss_time: moment().valueOf(),
      };
      dismissedReminders.push(reminder);
    });
    const formData = {
      appt: dismissedReminders,
    };
    this.calendarService.dismissAlarm(formData).subscribe(
      data => {
        this.remindersList = this.remindersList.filter(x => !x.checked);
        this.selectedReminders = [];
        if (this.remindersList.length === 0) {
          this.close();
        } else {
          this.selectedReminders[0] = this.remindersList[0];
          this.remindersList[0].checked = true;
        }
        this.getCalendars(false);
      },
      error => this.notificationService.showError(this.translateService.instant(error.error.errors.message))
    );
  }

  listenSocketEvents(): void {
    this.socketSubscription = this.socketEventStorage.getSocketMailEventInfoRefresher().subscribe(data => {
      if (data && data.length > 0) {
        this.getCalendars(false);
      }
    });
  }

  listenEventsRefresher(): void {
    this.eventsSubscription = this.calendarEventsRefreshersStorage.listenEventsRefresher().subscribe(data => {
      if (data) {
        this.getCalendars(false);
      }
    });
  }

  controlSnoozeOptions(): void {
    const _this = this;
    const selectedReminders = _this.remindersList.filter(reminderItem => reminderItem.checked === true);
    this.snoozeOptions.map((option, index) => {
      this.snoozeOptions[index]['disabled'] = false;
      if (option.negativeDuration === 1) {
        for (let i = 0; i < selectedReminders.length; i++) {
          if (
            new Date(moment(selectedReminders[i].alarm_info.alarm_instance_start).subtract(option.duration, option.type).valueOf()).getTime() <
            new Date().getTime()
          ) {
            this.snoozeOptions[index]['disabled'] = true;
            break;
          }
        }
      }
      if (option.key === 'SNOOZE_AT_EVENT_HOUR') {
        for (let i = 0; i < selectedReminders.length; i++) {
          if (new Date(moment(selectedReminders[i].alarm_info.alarm_instance_start).valueOf()).getTime() < new Date().getTime()) {
            this.snoozeOptions[index]['disabled'] = true;
            break;
          }
        }
      }
    });
  }

  select(checked, selectedReminder): void {
    this.remindersList.filter(reminder => reminder.appointment_id === selectedReminder.appointment_id)[0].checked = checked;
    this.selectedReminders = this.remindersList.filter(reminder => reminder.checked);
    this.controlSnoozeOptions();
  }

  listenRefresher(): void {
    // Subscribe to mail updater observable for quick action
    this.refreshSubscription = this.mailboxInvitationRefreshersStorage.listenAnswerRefresher().subscribe(data => {
      if (data === true) {
        this.getCalendars(false);
      }
    });
  }

  listenReminderModalOpenedRefresher(): void {
    this.modalOpenedSubscription = this.remindersStorage.listenReminderModalOpenedRefresher().subscribe(data => {
      if (data === true) {
        this.openModal(this.template);
      }
    });
  }
}
